import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";
import {
  TIMEZONES,
  getDisplayTzAbbr,
  getOfficeTzAbbrs,
  getTimeInZone,
} from "@/utils/time";

const CYCLE_INTERVAL_MS = 4500;
const TICK_INTERVAL_MS = 1000;
const CLOCK_DURATION = 1;
const DIGIT_STAGGER = 0.04;
const DIGIT_CYCLES = 2;
const LABEL_DURATION = 0.9;
const ICON_DURATION = 0.8;
const EASE = gsapEase("osmo");

const DAY_HOUR_START = 6;
const DAY_HOUR_END = 18;

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

type LabelApi = {
  update: (text: string) => void;
  destroy: () => void;
};

type ClockLineApi = {
  updateTime: (next: string) => void;
  snapTime: (next: string) => void;
  updateZone: (text: string) => void;
  destroy: () => void;
};

type LabelOptions = {
  align?: "left" | "center";
  /** Append stage to this node instead of replacing host contents. */
  appendTo?: HTMLElement;
};

export default function (element: HTMLElement) {
  const clockP = element.querySelector<HTMLElement>('[util-time="clock-p"]');
  const locP = element.querySelector<HTMLElement>('[util-time="loc-p"]');
  const iconNight = element.querySelector<HTMLElement>(
    '[util-time="icon-night"]',
  );
  const iconDay = element.querySelector<HTMLElement>('[util-time="icon-day"]');
  if (!clockP || !locP || !iconNight || !iconDay) return;

  const zones = (element.getAttribute("util-time-list") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((code) => ({ code: code.toUpperCase(), key: code.toLowerCase() }))
    .filter((z) => z.key in TIMEZONES);
  if (zones.length === 0) return;

  const intervalMs =
    parseFloat(element.getAttribute("util-time-interval") || "") ||
    CYCLE_INTERVAL_MS;

  const originalClock = clockP.textContent ?? "";
  const originalLoc = locP.textContent ?? "";

  let zoneIndex = 0;
  let cycleTimer: number | null = null;
  let tickTimer: number | null = null;
  let clockLine: ClockLineApi | null = null;
  let locLabel: LabelApi | null = null;
  let lastClock = "";
  let lastZone = "";
  let lastLoc = "";
  let lastIsDay: boolean | null = null;

  function read(zoneKey: string, when: Date = new Date()) {
    const { hour, minute } = getTimeInZone(zoneKey, when);
    return {
      timeStr: `${pad(hour)}:${pad(minute)}`,
      tzAbbr: getDisplayTzAbbr(zoneKey, when),
      isDay: hour >= DAY_HOUR_START && hour < DAY_HOUR_END,
    };
  }

  function applyState(animate: boolean) {
    const z = zones[zoneIndex];
    const { timeStr, tzAbbr, isDay } = read(z.key);

    if (timeStr !== lastClock) {
      if (animate) clockLine?.updateTime(timeStr);
      else clockLine?.snapTime(timeStr);
      lastClock = timeStr;
    }
    if (tzAbbr !== lastZone) {
      clockLine?.updateZone(tzAbbr);
      lastZone = tzAbbr;
    }
    if (z.code !== lastLoc) {
      locLabel?.update(z.code);
      lastLoc = z.code;
    }
    if (isDay !== lastIsDay) {
      setIcon(iconNight!, iconDay!, isDay, animate && lastIsDay !== null);
      lastIsDay = isDay;
    }
  }

  onMount(() => {
    const iconW = element.querySelector<HTMLElement>('[util-time="icon-w"]');
    if (iconW && !iconW.style.position) iconW.style.position = "relative";

    for (const el of [iconNight!, iconDay!]) {
      el.setAttribute("aria-hidden", "true");
      const svg = el.querySelector("svg");
      if (svg) {
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("focusable", "false");
      }
    }

    const seed = read(zones[0].key);

    const zoneAbbrs = new Set<string>();
    for (const z of zones) {
      for (const abbr of getOfficeTzAbbrs(z.key)) zoneAbbrs.add(abbr);
    }
    clockLine = buildClockLine(
      clockP!,
      seed.timeStr,
      seed.tzAbbr,
      [...zoneAbbrs],
    );
    locP!.style.textAlign = "center";
    locP!.style.margin = "0";
    locLabel = buildLabel(
      locP!,
      zones.map((z) => z.code),
      zones[0].code,
    );

    setIcon(iconNight!, iconDay!, seed.isDay, false);
    lastClock = seed.timeStr;
    lastZone = seed.tzAbbr;
    lastLoc = zones[0].code;
    lastIsDay = seed.isDay;

    tickTimer = window.setInterval(() => applyState(true), TICK_INTERVAL_MS);

    if (zones.length > 1) {
      cycleTimer = window.setInterval(() => {
        zoneIndex = (zoneIndex + 1) % zones.length;
        applyState(true);
      }, intervalMs);
    }
  });

  onDestroy(() => {
    if (cycleTimer) clearInterval(cycleTimer);
    if (tickTimer) clearInterval(tickTimer);
    cycleTimer = null;
    tickTimer = null;

    clockLine?.destroy();
    locLabel?.destroy();
    clockLine = null;
    locLabel = null;

    gsap.killTweensOf([iconNight!, iconDay!]);
    gsap.set([iconNight!, iconDay!], { clearProps: "transform,opacity" });

    clockP!.innerHTML = "";
    clockP!.textContent = originalClock;
    locP!.innerHTML = "";
    locP!.textContent = originalLoc;
    locP!.style.removeProperty("text-align");
    locP!.style.removeProperty("margin");
    clockP!.style.removeProperty("text-align");
    clockP!.style.removeProperty("margin");
  });
}

const CLOCK_ZONE_GAP = "0.25rem";

function buildClockLine(
  host: HTMLElement,
  initialTime: string,
  initialZone: string,
  zoneValues: string[],
): ClockLineApi {
  host.innerHTML = "";
  host.style.textAlign = "center";
  host.style.margin = "0";

  const row = document.createElement("span");
  row.setAttribute("util-time", "clock-row");
  Object.assign(row.style, {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    fontVariantNumeric: "tabular-nums",
    flexShrink: "0",
  });
  host.appendChild(row);

  const step = getLineHeightRatio(host);
  const digits = mountClockDigits(row, initialTime, step);

  const gap = document.createElement("span");
  gap.setAttribute("util-time", "clock-gap");
  gap.setAttribute("aria-hidden", "true");
  Object.assign(gap.style, {
    display: "block",
    flexShrink: "0",
    width: CLOCK_ZONE_GAP,
  });
  row.appendChild(gap);

  const zone = buildLabel(host, zoneValues, initialZone, {
    appendTo: row,
  });

  return {
    updateTime: digits.update,
    snapTime: digits.snap,
    updateZone: zone.update,
    destroy() {
      digits.destroy();
      zone.destroy();
    },
  };
}

type DigitSlots = {
  update: (next: string) => void;
  snap: (next: string) => void;
  destroy: () => void;
};

function mountClockDigits(
  parent: HTMLElement,
  initial: string,
  step: number,
): DigitSlots {
  type Slot = { roller: HTMLElement; current: number };
  const slots: Slot[] = [];
  const totalCells = 10 * DIGIT_CYCLES;
  const digits: number[] = [];
  for (let d = 0; d < totalCells; d++) digits.push(d % 10);
  const rollerStrip = digits.join("\n");

  for (const char of initial) {
    if (/\d/.test(char)) {
      const mask = document.createElement("span");
      mask.setAttribute("util-time-part", "mask");
      Object.assign(mask.style, {
        display: "inline-block",
        overflow: "clip",
        padding: "0.05em",
        margin: "-0.05em",
        height: `${step}em`,
        lineHeight: String(step),
        flexShrink: "0",
      });
      const roller = document.createElement("span");
      roller.setAttribute("util-time-part", "roller");
      Object.assign(roller.style, {
        display: "block",
        whiteSpace: "pre",
        textAlign: "center",
        willChange: "transform",
        lineHeight: String(step),
      });
      roller.textContent = rollerStrip;
      mask.appendChild(roller);
      parent.appendChild(mask);

      const startDigit = parseInt(char, 10);
      gsap.set(roller, { y: `${-startDigit * step}em` });
      slots.push({ roller, current: startDigit });
    } else {
      const span = document.createElement("span");
      span.setAttribute("util-time-part", "static");
      Object.assign(span.style, {
        display: "inline-block",
        overflow: "clip",
        padding: "0.05em",
        margin: "-0.05em",
        height: `${step}em`,
        lineHeight: String(step),
        flexShrink: "0",
      });
      span.textContent = char;
      parent.appendChild(span);
    }
  }

  function update(next: string) {
    const nextDigits = [...next].filter((c) => /\d/.test(c));
    if (nextDigits.length !== slots.length) return;

    nextDigits.forEach((d, i) => {
      const slot = slots[i];
      const endDigit = parseInt(d, 10);
      if (endDigit === slot.current) return;
      if (reduced) {
        gsap.set(slot.roller, { y: `${-endDigit * step}em` });
        slot.current = endDigit;
        return;
      }
      const target = endDigit > slot.current ? endDigit : 10 + endDigit;
      slot.current = endDigit;
      gsap.to(slot.roller, {
        y: `${-target * step}em`,
        duration: CLOCK_DURATION,
        ease: EASE,
        force3D: true,
        overwrite: "auto",
        delay: (slots.length - 1 - i) * DIGIT_STAGGER,
        onComplete: () => {
          gsap.set(slot.roller, { y: `${-endDigit * step}em` });
        },
      });
    });
  }

  function snap(next: string) {
    const nextDigits = [...next].filter((c) => /\d/.test(c));
    if (nextDigits.length !== slots.length) return;
    nextDigits.forEach((d, i) => {
      const slot = slots[i];
      const endDigit = parseInt(d, 10);
      gsap.set(slot.roller, { y: `${-endDigit * step}em` });
      slot.current = endDigit;
    });
  }

  function destroy() {
    slots.forEach((s) => gsap.killTweensOf(s.roller));
  }

  return { update, snap, destroy };
}

function syncMeasurerFont(from: HTMLElement, measurer: HTMLElement) {
  const cs = getComputedStyle(from);
  measurer.style.font = cs.font;
  measurer.style.letterSpacing = cs.letterSpacing;
  measurer.style.fontVariantNumeric = cs.fontVariantNumeric;
  measurer.style.textTransform = cs.textTransform;
}

function getLineHeightRatio(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  const lh = cs.lineHeight;
  if (lh === "normal") return 1.2;
  return parseFloat(lh) / parseFloat(cs.fontSize);
}

function buildLabel(
  host: HTMLElement,
  possibleValues: string[],
  initial: string,
  options: LabelOptions = {},
): LabelApi {
  const { align = "center", appendTo } = options;
  const isLeft = align === "left";
  const mount = appendTo ?? host;

  if (!appendTo) host.innerHTML = "";

  const stage = document.createElement("span");
  Object.assign(stage.style, {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: isLeft ? "flex-start" : "center",
    position: "relative",
    overflow: "clip",
    verticalAlign: "middle",
    lineHeight: "inherit",
  });

  let content = document.createElement("span");
  Object.assign(content.style, {
    display: "inline-block",
    whiteSpace: "pre",
    textAlign: isLeft ? "left" : "center",
    width: isLeft ? "auto" : "100%",
  });
  content.textContent = initial;

  // Measure on the host, not inside the clipped stage — otherwise width can
  // read too small once the stage has a narrow explicit width.
  const measurer = document.createElement("span");
  Object.assign(measurer.style, {
    position: "absolute",
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "pre",
    display: "inline-block",
    left: "0",
    top: "0",
  });
  host.appendChild(measurer);
  syncMeasurerFont(host, measurer);
  stage.appendChild(content);
  mount.appendChild(stage);

  const fontSize = parseFloat(getComputedStyle(host).fontSize) || 16;
  const WIDTH_PAD_EM = 0.125;
  const values = new Set<string>([initial, ...possibleValues]);
  let maxWidthEm = 0;
  let lineHeight = 0;
  const measureWidthEm = (text: string) => {
    measurer.textContent = text;
    return measurer.offsetWidth / fontSize + WIDTH_PAD_EM;
  };
  for (const text of values) {
    content.textContent = text;
    const w = measureWidthEm(text);
    if (w > maxWidthEm) maxWidthEm = w;
    if (content.offsetHeight > lineHeight) lineHeight = content.offsetHeight;
  }
  content.textContent = initial;

  stage.style.width = `${maxWidthEm}em`;
  stage.style.height = `${Math.ceil(lineHeight)}px`;
  stage.style.flexShrink = "0";

  // Absolutely positioned so swap clones can stack; centered in the stage
  // so short labels (EDT) don't sit high vs the clock row.
  Object.assign(content.style, {
    position: "absolute",
    left: "0",
    right: isLeft ? "auto" : "0",
    top: "50%",
    margin: "0",
  });
  gsap.set(content, { yPercent: -50 });

  const contentBaseStyle = {
    display: "inline-block",
    whiteSpace: "pre",
    textAlign: isLeft ? "left" : "center",
    width: isLeft ? "auto" : "100%",
    position: "absolute",
    left: "0",
    right: isLeft ? "auto" : "0",
    top: "50%",
  } as const;

  function update(text: string) {
    if (content.textContent === text) return;
    if (reduced) {
      content.textContent = text;
      gsap.set(content, { yPercent: -50 });
      return;
    }

    for (const child of Array.from(stage.children)) {
      if (child !== content) {
        gsap.killTweensOf(child);
        child.remove();
      }
    }
    gsap.killTweensOf(content);

    const out = content;

    const next = document.createElement("span");
    Object.assign(next.style, {
      ...contentBaseStyle,
      willChange: "transform, opacity",
    });
    next.textContent = text;
    stage.appendChild(next);
    content = next;

    gsap.set(out, { yPercent: -50, willChange: "transform, opacity" });

    gsap.to(out, {
      yPercent: -150,
      opacity: 0,
      duration: LABEL_DURATION,
      ease: EASE,
      onComplete: () => {
        out.remove();
      },
    });
    gsap.fromTo(
      next,
      { yPercent: 50, opacity: 0 },
      {
        yPercent: -50,
        opacity: 1,
        duration: LABEL_DURATION,
        ease: EASE,
        onComplete: () => {
          next.style.willChange = "";
        },
      },
    );
  }

  function destroy() {
    for (const child of Array.from(stage.children)) {
      gsap.killTweensOf(child);
    }
    measurer.remove();
  }

  return { update, destroy };
}

function setIcon(
  night: HTMLElement,
  day: HTMLElement,
  isDay: boolean,
  animate: boolean,
) {
  const show = isDay ? day : night;
  const hide = isDay ? night : day;

  if (!animate || reduced) {
    gsap.set(show, { xPercent: 0, yPercent: 0, opacity: 1 });
    gsap.set(hide, { xPercent: 100, yPercent: -100, opacity: 0 });
    return;
  }
  gsap.to(hide, {
    xPercent: 100,
    yPercent: -100,
    opacity: 0,
    duration: ICON_DURATION,
    ease: EASE,
    overwrite: "auto",
  });
  gsap.fromTo(
    show,
    { xPercent: -100, yPercent: 100, opacity: 0 },
    {
      xPercent: 0,
      yPercent: 0,
      opacity: 1,
      duration: ICON_DURATION,
      ease: EASE,
      overwrite: "auto",
    },
  );
}
